/**
 * Audit Evidence Service
 * 
 * API client for fetching evidence data from the Transaction Traceability API (Task 1.4).
 * Transforms backend evidence format to EvidenceContract for UI consumption.
 * 
 * API Endpoints (via vs-integrations):
 * - GET /.netlify/functions/audit/run/:run_id/evidence
 * - GET /.netlify/functions/audit/run/:run_id/transactions
 * - GET /.netlify/functions/audit/run/:run_id/transactions/:factor_name
 * 
 * @see vs-integrations/api/netlify/functions/audit.ts
 */

import { EvidenceContract, DisclosureLevel, MetricKey } from '@/types/evidenceContract';

// API configuration - uses vs-integrations backend
const INTEGRATIONS_API_BASE = import.meta.env.VITE_INTEGRATIONS_API_URL || 'https://vs-integrations-development.netlify.app';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

// =============================================================================
// API Response Types (match vs-integrations/api/src/services/audit/types.ts)
// =============================================================================

/**
 * Epic 65M: Run-level metadata for Trust Panel context
 * BUG-EPIC61-009: Added input counts for coverage display
 * BUG-UI-099: Added plaid_snapshot_id and run_ts for Sources tab
 */
export interface EvidenceRunMetadata {
  confidence_score: number | null;
  income_basis_used: 'DEPOSITED_NET' | 'ATTESTED' | 'VERIFIED_GROSS' | 'HYBRID' | null;
  debt_basis_used: 'CRS_BUREAU' | 'PLAID_LIABILITIES' | 'ATTESTED' | 'HYBRID' | null;
  coverage_months_available: number | null;
  coverage_months_used: number | null;
  data_freshness_ts: string | null;
  input_account_count: number | null;
  input_txn_count: number | null;
  input_months_coverage: number | null;
  // BUG-UI-099: Plaid snapshot and run timestamp for Sources tab
  plaid_snapshot_id: string | null;
  run_ts: string | null;
}

export interface EvidenceQueryResponse {
  run_id: string;
  member_id: string;
  total_evidence_records: number;
  filters_applied: {
    entity_type?: string;
    entity_name?: string;
    month_key?: string;
  };
  // Epic 65M: Run-level metadata for Trust Panel context
  run_metadata?: EvidenceRunMetadata;
  evidence: EvidenceQueryRecord[];
}

export interface EvidenceQueryRecord {
  evidence_id: string;
  entity_type: 'METRIC' | 'FACTOR' | 'FEATURE';
  entity_name: string;
  month_key?: string;
  evidence_level: 'INTERNAL' | 'EXTERNAL' | 'LENDER' | 'MEMBER';
  evidence_type: string;
  evidence_summary: string;
  intermediate_metrics?: Record<string, unknown>;
  // Calculation config from algo_run_evidence.calculation column (Epic 16 Click-to-Explain)
  calculation?: {
    formula?: string;
    steps?: Array<{ order: number; name: string; detail: string }>;
    configRef?: {
      gitSha?: string;
      rulesetVersion?: string;
      algorithmVersion?: string;
    };
  };
  reconciliation_status?: 'PASS' | 'WARN' | 'FAIL';
  reconciliation_delta?: number;
  created_at: string;
}

export interface TransactionContributionResponse {
  run_id: string;
  member_id: string;
  factor_name?: string;
  total_transactions: number;
  total_excluded: number;
  months: TransactionMonthData[];
}

export interface TransactionMonthData {
  month_key: string;
  transaction_ids: string[];
  excluded_transaction_ids: string[];
  exclusion_reasons: Record<string, string>;
  intermediate_metrics: {
    money_in: number;
    money_out: number;
    net_flow: number;
    income_txn_count: number;
    expense_txn_count: number;
    debt_payment_count: number;
    transfer_count: number;
  };
  top_contributors: TopContributorTransaction[];
  reconciliation?: {
    status: 'PASS' | 'WARN' | 'FAIL';
    delta: number;
    tolerance: number;
  };
}

export interface TopContributorTransaction {
  transaction_id: string;
  date: string;
  amount: number;
  description: string;
  category: string;
  role: 'INCOME' | 'EXPENSE' | 'DEBT_PAYMENT' | 'TRANSFER' | 'EXCLUDED';
  account_id?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetch evidence records for a specific algorithm run
 */
export async function getRunEvidence(
  runId: string,
  filters?: { entity_type?: string; entity_name?: string; month_key?: string }
): Promise<EvidenceQueryResponse> {
  const queryParams = new URLSearchParams();
  if (filters?.entity_type) queryParams.set('entity_type', filters.entity_type);
  if (filters?.entity_name) queryParams.set('entity_name', filters.entity_name);
  if (filters?.month_key) queryParams.set('month_key', filters.month_key);

  const url = `${INTEGRATIONS_API_BASE}/.netlify/functions/audit/run/${runId}/evidence${queryParams.toString() ? `?${queryParams}` : ''}`;
  
  console.log(`📡 [auditEvidenceService] Fetching evidence: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch evidence: ${response.status} - ${errorText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Fetch transaction contributions for a specific algorithm run
 */
export async function getRunTransactions(
  runId: string,
  factorName?: string
): Promise<TransactionContributionResponse> {
  const path = factorName 
    ? `/.netlify/functions/audit/run/${runId}/transactions/${factorName}`
    : `/.netlify/functions/audit/run/${runId}/transactions`;
  
  const url = `${INTEGRATIONS_API_BASE}${path}`;
  
  console.log(`📡 [auditEvidenceService] Fetching transactions: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch transactions: ${response.status} - ${errorText}`);
  }

  const data = await response.json();
  return data.data || data;
}

// =============================================================================
// TRANSFORMER: API Response → EvidenceContract
// =============================================================================

/**
 * Transform API evidence response to EvidenceContract format for UI
 */
export function transformToEvidenceContract(
  evidenceRecord: EvidenceQueryRecord,
  transactions?: TransactionContributionResponse,
  // TODO: RBAC - Re-enable disclosure levels when role-based security is implemented
  // For now, default to AUDITOR to show all data during development/testing
  disclosureLevel: DisclosureLevel = 'AUDITOR'
): EvidenceContract {
  // BUG-UI-045: Handle case where intermediate_metrics is a JSON string instead of object
  // This can happen if the backend serializes it twice or if database returns it as string
  let metrics: Record<string, unknown> = {};
  const rawMetrics = evidenceRecord.intermediate_metrics;

  if (rawMetrics) {
    if (typeof rawMetrics === 'string') {
      try {
        metrics = JSON.parse(rawMetrics);
        console.log('🔄 [transformToEvidenceContract] Parsed JSON string metrics');
      } catch {
        console.warn('⚠️ [transformToEvidenceContract] Failed to parse intermediate_metrics string');
        metrics = {};
      }
    } else if (typeof rawMetrics === 'object') {
      metrics = rawMetrics;
    }
  }

  // DEBUG: Log transformation inputs
  console.log('🔄 [transformToEvidenceContract] Input:', {
    entity_name: evidenceRecord.entity_name,
    intermediate_metrics_type: typeof rawMetrics,
    intermediate_metrics: rawMetrics,
    metrics_keys: Object.keys(metrics),
  });

  // Extract value from intermediate_metrics based on entity_name
  const value = extractMetricValue(evidenceRecord.entity_name, metrics);
  const unit = determineUnit(evidenceRecord.entity_name);

  // DEBUG: Log extracted values
  console.log('🔄 [transformToEvidenceContract] Extracted:', {
    value,
    unit,
    label: formatLabel(evidenceRecord.entity_name),
  });

  // BUG-EPIC61-011 FIX: Use month_key for as_of date, not created_at
  // The as_of field should represent the month the metric is for, not when it was calculated
  // For monthly metrics: use month_key (e.g., "2025-12" → "2025-12-01")
  // For point-in-time metrics: use created_at
  const asOfDate = evidenceRecord.month_key
    ? `${evidenceRecord.month_key}-01T00:00:00Z`  // Use month start for monthly metrics
    : evidenceRecord.created_at;                   // Use creation time for point-in-time

  // Build the EvidenceContract
  const contract: EvidenceContract = {
    run_id: '', // Will be set by caller
    metric_key: evidenceRecord.entity_name as MetricKey,
    label: formatLabel(evidenceRecord.entity_name),
    value,
    unit,
    period: {
      type: evidenceRecord.month_key ? 'MONTH' : 'AS_OF',
      start: evidenceRecord.month_key ? `${evidenceRecord.month_key}-01` : undefined,
    },
    as_of: asOfDate,
    summary: {
      display_value: formatValue(value, unit),
      one_liner: evidenceRecord.evidence_summary,
      trend: undefined, // Would need historical data
    },
    // BUG-UI-061: Pass through intermediate_metrics for income_basis access in UI
    intermediate_metrics: metrics,
  };

  // Add components for LENDER+ disclosure
  if (disclosureLevel !== 'MEMBER' && metrics) {
    contract.components = buildComponents(evidenceRecord, metrics);
  }

  // Add calculation details for LENDER+ (formula/steps visible, weights/thresholds AUDITOR only)
  // Epic 16 Click-to-Explain: Read from API response, fallback to building from intermediate_metrics
  if (disclosureLevel !== 'MEMBER') {
    const apiCalc = evidenceRecord.calculation;
    const configRef = apiCalc?.configRef;

    // BUG-UI-044: Only show formula if API provides it, or if we have actual calculation data
    const hasApiFormula = !!apiCalc?.formula;
    const hasCalculationData = metrics && Object.keys(metrics).length > 0;

    // BUG-UI-041 FIX: Always build steps from intermediate_metrics when available
    // API steps are often generic (e.g., "curve_lookup(x) → 72") and miss the rich input values
    // We ALWAYS want to show the intermediate values so users can verify the calculation
    const builtSteps = hasCalculationData ? buildCalculationSteps(evidenceRecord.entity_name, metrics) : [];
    const hasBuiltSteps = builtSteps.length > 0;

    // Only fall back to API steps if we couldn't build any from intermediate_metrics
    const hasApiSteps = apiCalc?.steps && apiCalc.steps.length > 0;

    contract.calculation = {
      // Use API formula if available, otherwise build from entity name (only if we have data)
      formula: hasApiFormula ? apiCalc.formula : (hasCalculationData ? buildFormula(evidenceRecord.entity_name) : 'Formula pending - calculation data not yet available'),
      // BUG-UI-041 FIX: Prefer built steps (rich intermediate values) over generic API steps
      steps: hasBuiltSteps ? builtSteps : (hasApiSteps ? apiCalc.steps : []),
      // Use API config_ref (transform camelCase to snake_case for UI contract)
      config_ref: {
        ruleset_version: configRef?.rulesetVersion || 'tx_rules_v3.2',
        git_sha: configRef?.gitSha || 'N/A',
        algorithm_version: configRef?.algorithmVersion || 'verascore_v2.3',
      },
      // Weights and thresholds only for AUDITOR
      ...(disclosureLevel === 'AUDITOR' && {
        weights: {},
        thresholds: {},
      }),
    };

    // Add sources if transactions provided
    if (transactions) {
      contract.sources = buildSources(transactions);
    }
  }

  return contract;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function extractMetricValue(entityName: string, metrics: Record<string, unknown>): number {
  // BUG-UI-045: Fix value extraction for total_* fields
  // Backend emits: total_cash_on_hand, total_debt, minimum_payments_all_debt, total_actual_debt_payments
  const valueMap: Record<string, string[]> = {
    // METRIC entity names (CANONICAL_METRICS from backend)
    'money_in_1m': ['sum_deposits', 'money_in', 'total'],
    'money_out_1m': ['sum_withdrawals', 'money_out', 'total'],
    'money_leftover_1m': ['money_left_over', 'money_leftover', 'net_flow'],
    'net_savings_1m': ['net_savings', 'savings', 'total'],
    // BUG-UI-045: cash_on_hand - backend emits 'total_cash_on_hand'
    'cash_on_hand': ['total_cash_on_hand', 'total_balance', 'cash_on_hand', 'balance'],
    'dti_ratio': ['dti_ratio', 'dti', 'ratio'],
    // BUG-LENDER-MINPAY-MISMATCH-001: Prefer minimum_payments_all_debt (spec-defined DTI input) over actual payments
    'monthly_debt_payments': ['minimum_payments_all_debt', 'total_actual_debt_payments', 'minimum_payments', 'debt_payments', 'total'],
    // BUG-UI-045: total_debt - backend emits 'total_debt' directly
    'total_debt': ['total_debt', 'outstanding_debt', 'debt_total', 'short_term_debt_balance', 'loan_balance'],
    'gross_income_6m': ['sum_money_in', 'money_in', 'total'],
    'money_in_6m': ['sum_money_in', 'money_in', 'total'],
    'money_out_6m': ['sum_money_out', 'money_out', 'total'],
    'money_leftover_6m': ['money_left_over', 'net_flow', 'total'],
    // BUG-UI-045: late_payments and credit_score
    'late_payments': ['number_of_late_payments', 'late_payments', 'late_payment_count', 'count'],
    'credit_score': ['credit_score', 'fico_score', 'score'],
    // FACTOR entity names (CANONICAL_FACTORS from backend)
    'cash_flow_factor': ['score', 'factor_score', 'score_float'],
    'debt_to_income_factor': ['score', 'factor_score', 'score_float'],
    'liquid_reserves_factor': ['score', 'factor_score', 'score_float'],
    'savings_factor': ['score', 'factor_score', 'score_float'],
    'debt_management_factor': ['score', 'factor_score', 'score_float'],
    'payment_history_factor': ['score', 'factor_score', 'score_float'],
    // VeraScore
    'verascore_current': ['score', 'verascore', 'final_score'],
  };

  // BUG-UI-045: Enhanced null/undefined checking
  const keys = valueMap[entityName] || ['value', 'score', 'total', 'factor_score'];

  // DEBUG: Log what we're looking for
  console.log(`🔍 [extractMetricValue] entityName=${entityName}, looking for keys:`, keys);
  console.log(`🔍 [extractMetricValue] metrics keys available:`, Object.keys(metrics));
  console.log(`🔍 [extractMetricValue] metrics object:`, JSON.stringify(metrics));

  // BUG-UI-045: CRITICAL FIX - For total_debt, sum the components if total_debt itself is 0 or missing
  // Backend emits: { short_term_debt_balance: X, loan_balance: Y, total_debt: X+Y }
  // Sometimes total_debt is 0 but components have values
  if (entityName === 'total_debt') {
    const totalDebt = metrics['total_debt'];
    const shortTermDebt = metrics['short_term_debt_balance'];
    const loanBalance = metrics['loan_balance'];

    // If total_debt exists and is non-zero, use it
    if (totalDebt !== undefined && totalDebt !== null && Number(totalDebt) !== 0) {
      console.log(`✅ [extractMetricValue] Found total_debt=${totalDebt} for ${entityName}`);
      return Number(totalDebt);
    }

    // Otherwise, sum the components
    const stDebt = typeof shortTermDebt === 'number' ? shortTermDebt : 0;
    const lnBalance = typeof loanBalance === 'number' ? loanBalance : 0;
    if (stDebt > 0 || lnBalance > 0) {
      const sum = stDebt + lnBalance;
      console.log(`✅ [extractMetricValue] Computed total_debt=${sum} from components (st=${stDebt}, ln=${lnBalance})`);
      return sum;
    }
  }

  // BUG-UI-045: CRITICAL FIX - For cash_on_hand, use total_cash_on_hand or sum account balances
  if (entityName === 'cash_on_hand') {
    const totalCash = metrics['total_cash_on_hand'];
    if (totalCash !== undefined && totalCash !== null && Number(totalCash) !== 0) {
      console.log(`✅ [extractMetricValue] Found total_cash_on_hand=${totalCash} for ${entityName}`);
      return Number(totalCash);
    }

    // Try to sum from account_breakdown if available
    const accountBreakdown = metrics['account_breakdown'] as Array<{ balance: number }> | undefined;
    if (accountBreakdown && Array.isArray(accountBreakdown)) {
      const sum = accountBreakdown.reduce((acc, item) => acc + (item.balance || 0), 0);
      if (sum > 0) {
        console.log(`✅ [extractMetricValue] Computed cash_on_hand=${sum} from account_breakdown`);
        return sum;
      }
    }
  }

  // BUG-LENDER-MINPAY-MISMATCH-001: Prioritize minimum_payments_all_debt (the 2.5% estimate)
  // because the dashboard table shows this value and it's the spec-defined input for DTI/LR.
  // total_actual_debt_payments (observed transaction payments) is kept as a fallback and
  // remains available in intermediate_metrics for the breakdown tab.
  if (entityName === 'monthly_debt_payments') {
    const minPayments = metrics['minimum_payments_all_debt'];
    const actualPayments = metrics['total_actual_debt_payments'];
    const loanPayments = metrics['total_loan_payments'];
    const creditPayments = metrics['total_credit_payments'];

    if (minPayments !== undefined && minPayments !== null && Number(minPayments) !== 0) {
      console.log(`✅ [extractMetricValue] Found minimum_payments_all_debt=${minPayments} for ${entityName}`);
      return Number(minPayments);
    }
    if (actualPayments !== undefined && actualPayments !== null && Number(actualPayments) !== 0) {
      console.log(`✅ [extractMetricValue] Found total_actual_debt_payments=${actualPayments} for ${entityName}`);
      return Number(actualPayments);
    }
    // Sum loan + credit payments as fallback
    const lp = typeof loanPayments === 'number' ? loanPayments : 0;
    const cp = typeof creditPayments === 'number' ? creditPayments : 0;
    if (lp > 0 || cp > 0) {
      const sum = lp + cp;
      console.log(`✅ [extractMetricValue] Computed debt_payments=${sum} from loan+credit (lp=${lp}, cp=${cp})`);
      return sum;
    }
  }

  for (const key of keys) {
    const val = metrics[key];
    // BUG-UI-045: Check for both undefined AND null, and ensure it's a valid number
    if (val !== undefined && val !== null && !Number.isNaN(Number(val))) {
      const numVal = Number(val);
      console.log(`✅ [extractMetricValue] Found ${key}=${numVal} for ${entityName}`);
      return numVal;
    }
  }

  // BUG-UI-045: Fallback - try to find any numeric field that matches common patterns
  const fallbackPatterns: Record<string, RegExp[]> = {
    'monthly_debt_payments': [/debt.*payment/i, /payment.*debt/i, /actual.*debt/i],
    'cash_on_hand': [/cash.*hand/i, /liquid.*reserve/i, /total.*balance/i, /total_cash/i],
    'total_debt': [/total.*debt/i, /outstanding.*debt/i],
  };

  const patterns = fallbackPatterns[entityName];
  if (patterns) {
    for (const [key, val] of Object.entries(metrics)) {
      if (typeof val === 'number' && patterns.some(p => p.test(key))) {
        console.log(`✅ [extractMetricValue] Fallback pattern found ${key}=${val} for ${entityName}`);
        return val;
      }
    }
  }

  // BUG-UI-045: Ultimate fallback - find the first numeric value that looks like a main value
  // This handles cases where the key name doesn't match expected patterns
  const mainValueKeys = ['total', 'sum', 'value', 'amount', 'balance'];
  for (const [key, val] of Object.entries(metrics)) {
    if (typeof val === 'number' && !key.includes('count') && !key.includes('txn')) {
      // Check if key contains any main value indicator
      const lowerKey = key.toLowerCase();
      if (mainValueKeys.some(mvk => lowerKey.includes(mvk))) {
        console.log(`✅ [extractMetricValue] Ultimate fallback found ${key}=${val} for ${entityName}`);
        return val;
      }
    }
  }

  console.warn(`⚠️ [extractMetricValue] No value found for ${entityName}, returning 0`);
  return 0;
}

function determineUnit(entityName: string): 'USD' | 'PERCENT' | 'COUNT' | 'RATIO' | 'MONTHS' {
  // BUG-UI-035, BUG-UI-037: Factor scores and VeraScore are 0-100 integers, NOT percentages
  // They should display as "44" not "44.0%"

  // DTI ratio is actually a percentage (e.g., 15.6% DTI)
  if (entityName.includes('dti_ratio') || entityName.includes('rate')) return 'PERCENT';

  // Factors and VeraScore are 0-100 scale scores - use COUNT to display as integers
  if (entityName.includes('factor') || entityName.includes('verascore')) return 'COUNT';

  // Explicitly handle credit_score as COUNT (0-850 scale)
  if (entityName === 'credit_score') return 'COUNT';

  // Late payments is a count
  if (entityName.includes('late_payments') || entityName.includes('count')) return 'COUNT';

  // Months coverage
  if (entityName.includes('months')) return 'MONTHS';

  // Default to USD for monetary values
  return 'USD';
}

/**
 * Format entity name to human-readable label
 * BUG-UI-010: Modal title shows technical name instead of user-friendly name
 */
function formatLabel(entityName: string): string {
  // Map of entity names to user-friendly labels
  const labelMap: Record<string, string> = {
    // Metrics
    // BUG-UI-145: Labels must match what the user sees in the dashboard table (FINANCIAL_METRICS in constants.ts)
    // to avoid cognitive friction when clicking a row and seeing a different title in the modal
    'money_in_1m': 'Gross Income',  // BUG-UI-145: Was "Money In (Monthly)" — table says "Gross Income"
    'money_out_1m': 'Monthly Expenses',  // BUG-UI-145: Was "Money Out (Monthly)" — table says "Monthly Expenses"
    'money_leftover_1m': 'Money Leftover',
    'net_savings_1m': 'Net Savings',  // BUG-UI-145: Was "Net Savings (Monthly)" — table says "Net Savings"
    'cash_on_hand': 'Cash on Hand',
    'dti_ratio': 'Debt-to-Income Ratio',
    'monthly_debt_payments': 'Minimum Debt Payments',
    'total_debt': 'Outstanding Debt',
    'late_payments': 'Late Payments',
    'credit_score': 'Credit Score',
    // BUG-UI-029: Label for yearly income modal
    // VS-BUG-001: This is the YEARLY income modal — opened from "Annual Deposited Income" label in header
    'gross_income_6m': 'Annual Deposited Income',
    'money_in_6m': 'Money In (6 Month)',
    'money_out_6m': 'Money Out (6 Month)',
    'money_leftover_6m': 'Money Leftover (6 Month)',
    // Factors - BUG-UI-040 FIX: Use consistent terminology across dashboard and modal
    // Note: These are FACTOR SCORES (0-100), not the underlying metric values
    'cash_flow_factor': 'Cash Flow Score',
    'debt_to_income_factor': 'DTI Score',
    'liquid_reserves_factor': 'Liquid Reserves Score',  // BUG-UI-040: Aligned with dashboard label
    'savings_factor': 'Savings Score',
    'debt_management_factor': 'Debt Management Score',
    'payment_history_factor': 'Payment History Score',
    // VeraScore
    'verascore_current': 'Current VeraScore',
    'verascore_6m_avg': 'VeraScore (6 Month Avg)',
    'verascore_12m_avg': 'VeraScore (12 Month Avg)',
    'verascore_24m_avg': 'VeraScore (24 Month Avg)',
  };

  // Return mapped label if exists
  if (labelMap[entityName]) {
    return labelMap[entityName];
  }

  // Fallback: format the entity name nicely
  return entityName
    .replace(/_/g, ' ')
    .replace(/(\d+)m\b/g, '($1 mo)')
    .replace(/\bfactor\b/gi, 'Factor')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatValue(value: number, unit: string): string {
  switch (unit) {
    case 'USD': return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    case 'PERCENT': return `${value.toFixed(1)}%`;
    case 'RATIO': return value.toFixed(2);
    case 'COUNT': return value.toLocaleString();
    case 'MONTHS': return `${value} months`;
    default: return String(value);
  }
}

function buildComponents(record: EvidenceQueryRecord, metrics: Record<string, unknown>) {
  const items: Array<{ name: string; value?: number; count?: number; percentage?: number; group?: string }> = [];

  console.log('[buildComponents] entity_type:', record.entity_type, 'metrics:', metrics);

  // BUG-UI-033/054: Special handling for VeraScore to show hierarchical factor breakdown
  // BUG-UI-054/069: Added fallback keys for each factor since backend may use different naming
  // Backend curve scoring uses 'stage1_*' prefix for Stage 1 factors (DTI, Debt, PayHist)
  // and 'stage2_*' prefix for Stage 2 factors (Cash Flow, Savings, Liquid Reserves)
  if (record.entity_name === 'verascore_current') {
    // BUG-UI-054 FIX: Define factor groups with EXACT keys from backend (vsVeraScoreCalculateStep.ts)
    // Backend curve scoring emits:
    // - Stage 1 (Other Composite): stage1_debt_to_income_score, stage1_liquid_reserves_score, stage1_debt_management_score, stage1_payment_history_score
    // - Stage 2 (Final): cash_flow_score, savings_score, liquid_reserves_score (NO stage2_ prefix!)
    // Note: liquid_reserves appears twice - once with stage1_ prefix (for Other Composite) and once without (for final score)
    const factorGroups = {
      'Stage 2 Factors': [
        // Stage 2 factors use NO prefix - these are the direct curve lookups
        { keys: ['cash_flow_score', 'stage2_cash_flow_score', 'cash_flow_factor', 'cashflow_score'], name: 'Cash Flow', weight: 0.15 },
        { keys: ['savings_score', 'stage2_savings_score', 'savings_factor', 'net_savings_score'], name: 'Savings', weight: 0.12 },
        { keys: ['liquid_reserves_score', 'stage2_liquid_reserves_score', 'liquid_reserves_factor', 'reserves_score'], name: 'Liquid Reserves', weight: 0.12 },
      ],
      'Stage 1 Factors (Other Composite)': [
        // Stage 1 factors use stage1_ prefix - these feed into other_composite calculation
        { keys: ['stage1_debt_to_income_score', 'debt_to_income_score', 'dti_score', 'debt_to_income_factor'], name: 'Debt-to-Income', weight: 0.20 },
        { keys: ['stage1_debt_management_score', 'debt_management_score', 'debt_management_factor', 'debt_mgmt_score'], name: 'Debt Management', weight: 0.175 },
        { keys: ['stage1_payment_history_score', 'payment_history_score', 'payment_history_factor', 'pmt_history_score'], name: 'Payment History', weight: 0.10 },
      ],
    };

    let totalWeight = 0;
    for (const [groupName, factors] of Object.entries(factorGroups)) {
      for (const factor of factors) {
        // BUG-UI-054: Try multiple key names to find the score
        let score: number | undefined;
        for (const key of factor.keys) {
          const val = metrics[key];
          if (typeof val === 'number') {
            score = val;
            console.log(`[buildComponents] Found ${factor.name} score: ${key}=${score}`);
            break;
          }
        }
        if (typeof score === 'number') {
          const contribution = score * factor.weight;
          totalWeight += factor.weight;
          // BUG-UI-081 FIX: Add explicit weight field for Breakdown tab display
          // BUG-UI-082: Include formula showing Score × Weight = Weighted Score
          const weightedScore = (score / 100) * factor.weight; // Normalized: score 0-100 → 0-1, then × weight
          items.push({
            name: factor.name,
            value: Math.round(score),
            percentage: factor.weight * 100,
            group: groupName,
            weight: factor.weight, // BUG-UI-081: Explicit weight for display
            formula: `${Math.round(score)} × ${factor.weight.toFixed(3)} = ${weightedScore.toFixed(3)}`, // BUG-UI-082
          });
        } else {
          console.log(`[buildComponents] Missing ${factor.name} - tried keys: ${factor.keys.join(', ')}`);
        }
      }
    }

    // BUG-UI-069: Add other_composite for 2-stage calculation clarity
    const otherComposite = metrics['other_composite'] as number | undefined;
    if (typeof otherComposite === 'number') {
      const ocWeight = 0.61; // Stage 1 contributes 61% to final score
      const ocWeightedScore = (otherComposite / 100) * ocWeight;
      items.push({
        name: 'Other Composite (Stage 1)',
        value: Math.round(otherComposite),
        percentage: ocWeight * 100, // Stage 1 weight in final calculation
        group: 'Core Factors', // Group with core factors
        weight: ocWeight, // BUG-UI-081
        formula: `${Math.round(otherComposite)} × ${ocWeight.toFixed(3)} = ${ocWeightedScore.toFixed(3)}`, // BUG-UI-082
      });
    }

    // Add total/weighted sum if available
    const finalScore = metrics['score'] as number | undefined;
    if (typeof finalScore === 'number') {
      items.push({
        name: 'Final VeraScore',
        value: Math.round(finalScore),
        percentage: 100,
        group: 'Result',
      });
    }

    return {
      count: items.length,
      breakdown_type: 'VERASCORE' as const,
      items,
    };
  }

  // For FACTOR entities, show the key metrics as breakdown items
  if (record.entity_type === 'FACTOR') {
    // EPIC 62 Phase 2B: Prefer factor_audit_dto when available (single source of truth)
    const factorAuditDTO = metrics.factor_audit_dto as {
      inputs?: Array<{
        name: string;
        value: number;
        basis?: string;
        source_type?: string;
        freshness?: { days_old: number; tier: string };
        evidence_summary?: string;
        drilldown_data?: any;
      }>;
      derived?: Array<{
        name: string;
        value: number;
        formula_display?: string;
        intermediates?: Array<{ name: string; value: number }>;
      }>;
      mapping?: {
        computed_score: number;
        curve_or_bands?: string;
        selected_segment?: string;
        curve_formula?: string;
      };
      confidence?: {
        score: number;
        tier: string;
        drivers?: Array<{ name: string; impact: number; description: string }>;
      };
    } | undefined;

    if (factorAuditDTO && (factorAuditDTO.inputs || factorAuditDTO.derived)) {
      console.log('[buildComponents] EPIC 62 Phase 2B: Using factor_audit_dto as single source of truth');
      const breakdownItems: Array<{
        name: string;
        value: number;
        breakdown_group?: 'INPUTS' | 'DERIVED' | 'RESULT';
        source_badge?: string;
        formula?: string;
        drilldown_data?: any;
      }> = [];

      // Add INPUTS from factor_audit_dto.inputs
      if (factorAuditDTO.inputs) {
        for (const input of factorAuditDTO.inputs) {
          // BUG-UI-096: Transform drilldown_data to map 'value' to 'amount' for frontend compatibility
          let transformedDrilldown = input.drilldown_data;
          if (input.drilldown_data?.included_items) {
            transformedDrilldown = {
              ...input.drilldown_data,
              included_items: input.drilldown_data.included_items.map((item: { name: string; value: number; source: string }) => ({
                name: item.name,
                amount: item.value,  // Backend uses 'value', frontend expects 'amount'
                source: item.source,
                confidence: 'HIGH' as const
              }))
            };
          }
          breakdownItems.push({
            name: input.name,
            value: input.value,
            breakdown_group: 'INPUTS',
            source_badge: input.basis, // basis maps to source_badge
            drilldown_data: transformedDrilldown,
          });
        }
      }

      // Add DERIVED from factor_audit_dto.derived
      if (factorAuditDTO.derived) {
        for (const derived of factorAuditDTO.derived) {
          breakdownItems.push({
            name: derived.name,
            value: derived.value,
            breakdown_group: 'DERIVED',
            formula: derived.formula_display,
          });
        }
      }

      // Add RESULT from factor_audit_dto.mapping
      if (factorAuditDTO.mapping) {
        breakdownItems.push({
          name: `${record.entity_name.replace(/_/g, ' ').replace(/factor/i, 'Score').trim()}`,
          value: factorAuditDTO.mapping.computed_score,
          breakdown_group: 'RESULT',
          formula: factorAuditDTO.mapping.curve_formula || `${factorAuditDTO.mapping.selected_segment} → Score via ${factorAuditDTO.mapping.curve_or_bands?.toLowerCase() || 'curve'} mapping`,
        });
      }

      return {
        count: breakdownItems.length,
        breakdown_type: 'METRIC' as const,
        items: breakdownItems,
      };
    }

    // EPIC 62: Check for component_breakdown array (legacy format)
    const componentBreakdown = metrics.component_breakdown as Array<{
      name: string;
      value: number | string;
      breakdown_group?: 'INPUTS' | 'DERIVED' | 'RESULT';
      source_badge?: 'ATTESTED' | 'DEPOSITED' | 'PLAID' | 'CRS' | 'HYBRID' | 'VERIFIED';
      formula?: string;
      drilldown_data?: any;
    }> | undefined;

    if (componentBreakdown && Array.isArray(componentBreakdown) && componentBreakdown.length > 0) {
      console.log('[buildComponents] EPIC 62: Using component_breakdown array (legacy):', componentBreakdown);

      // Transform component_breakdown to ComponentItem format
      const breakdownItems = componentBreakdown.map(comp => ({
        name: comp.name,
        value: typeof comp.value === 'number' ? comp.value : parseFloat(String(comp.value)),
        breakdown_group: comp.breakdown_group,
        source_badge: comp.source_badge,
        formula: comp.formula,
        drilldown_data: comp.drilldown_data,
      }));

      return {
        count: breakdownItems.length,
        breakdown_type: 'METRIC' as const,
        items: breakdownItems,
      };
    }

    // FALLBACK: Old logic for backward compatibility (if component_breakdown not present)
    console.log('[buildComponents] EPIC 62: component_breakdown not found, using fallback logic');

    // Map of metric keys to display names and formatting
    const factorMetricMap: Record<string, { label: string; format: 'currency' | 'percent' | 'ratio' | 'number' }> = {
      // DTI Factor
      yearly_gross_income: { label: 'Yearly Gross Income', format: 'currency' },
      minimum_payments_all_debt: { label: 'Min Debt Payments (Monthly)', format: 'currency' },
      dti_ratio: { label: 'DTI Ratio', format: 'percent' },
      // Cash Flow Factor
      money_in: { label: 'Money In', format: 'currency' },
      money_out: { label: 'Money Out', format: 'currency' },
      net_cash_flow: { label: 'Net Cash Flow', format: 'currency' },
      cash_flow_margin: { label: 'Cash Flow Margin', format: 'percent' },
      cash_flow_pct: { label: 'Cash Flow %', format: 'percent' },
      money_left_over: { label: 'Money Left Over', format: 'currency' },
      // Liquid Reserves Factor
      liquid_balance: { label: 'Liquid Balance', format: 'currency' },
      months_coverage: { label: 'Months of Coverage', format: 'number' },
      // Savings Factor
      net_savings: { label: 'Net Savings', format: 'currency' },
      savings_rate: { label: 'Savings Rate', format: 'percent' },
      // Debt Management Factor
      total_debt_payments: { label: 'Total Debt Payments', format: 'currency' },
      required_payments: { label: 'Required Payments', format: 'currency' },
      debt_payment_ratio: { label: 'Payment Ratio', format: 'percent' },
      // Payment History Factor
      late_payments: { label: 'Late Payments', format: 'number' },
      on_time_payments: { label: 'On-Time Payments', format: 'number' },
    };

    // Build items from available metrics - first try mapped metrics
    const addedKeys = new Set<string>();
    for (const [key, config] of Object.entries(factorMetricMap)) {
      if (metrics[key] !== undefined) {
        const rawValue = Number(metrics[key]);
        items.push({
          name: config.label,
          value: rawValue,
        });
        addedKeys.add(key);
      }
    }

    // Then add any remaining numeric metrics not in the map (exclude metadata fields)
    const excludeKeys = new Set(['score', 'score_float', 'rating', 'scoring_method', 'component_breakdown']);
    for (const [key, val] of Object.entries(metrics)) {
      if (typeof val === 'number' && !addedKeys.has(key) && !excludeKeys.has(key)) {
        items.push({
          name: formatMetricName(key),
          value: val,
        });
      }
    }

    console.log('[buildComponents] FACTOR items (fallback):', JSON.stringify(items));

    // Calculate percentages for LENDER level display (even though METRIC breakdown doesn't show pie chart)
    const totalValue = items.reduce((sum, item) => sum + Math.abs(item.value || 0), 0);
    const itemsWithPercentage = items.map(item => ({
      ...item,
      percentage: totalValue > 0 ? (Math.abs(item.value || 0) / totalValue) * 100 : 0,
    }));

    return {
      count: items.length,
      breakdown_type: 'METRIC' as const,
      items: itemsWithPercentage,
    };
  }

  // For METRIC entities, build component breakdown

  // BUG-UI-047: Check for account_breakdown array with real account names
  // Backend sends: account_breakdown: [{ name: "Alex's Savings", type: "depository", subtype: "savings", balance: 5000, percentage: 25 }, ...]
  const accountBreakdown = metrics.account_breakdown as Array<{
    name: string;
    type: string;
    subtype: string;
    balance: number;
    percentage: number;
    account_id?: string;
  }> | undefined;

  if (accountBreakdown && Array.isArray(accountBreakdown) && accountBreakdown.length > 0) {
    console.log('[buildComponents] BUG-UI-047: Using account_breakdown array:', accountBreakdown);
    const accountItems = accountBreakdown.map(acc => ({
      name: acc.name || `${acc.subtype || acc.type} Account`,
      value: acc.balance,
      percentage: acc.percentage,
    }));
    return {
      count: accountItems.length,
      breakdown_type: 'ACCOUNT' as const,
      items: accountItems,
    };
  }

  // Map of metric keys to display names for deposit/money breakdown
  const metricComponentMap: Record<string, { label: string; format: 'currency' | 'count' }> = {
    // Money In components
    checking_deposits: { label: 'Checking Deposits', format: 'currency' },
    saving_deposits: { label: 'Savings Deposits', format: 'currency' },
    investment_deposits: { label: 'Investment Deposits', format: 'currency' },
    sum_deposits: { label: 'Total Deposits', format: 'currency' },
    // Money Out components
    checking_withdrawals: { label: 'Checking Withdrawals', format: 'currency' },
    saving_withdrawals: { label: 'Savings Withdrawals', format: 'currency' },
    investment_withdrawals: { label: 'Investment Withdrawals', format: 'currency' },
    sum_withdrawals: { label: 'Total Withdrawals', format: 'currency' },
    // Generic value components
    sum_amount: { label: 'Total Amount', format: 'currency' },
    avg_amount: { label: 'Average Amount', format: 'currency' },
    min_amount: { label: 'Minimum Amount', format: 'currency' },
    max_amount: { label: 'Maximum Amount', format: 'currency' },
  };

  // Try to build METRIC breakdown from component values first
  console.log('[buildComponents] metricComponentMap keys:', Object.keys(metricComponentMap));
  console.log('[buildComponents] metrics keys:', Object.keys(metrics));
  for (const [key, config] of Object.entries(metricComponentMap)) {
    const hasKey = metrics[key] !== undefined;
    const isNotSum = key !== 'sum_deposits' && key !== 'sum_withdrawals' && key !== 'sum_amount';
    console.log(`[buildComponents] Checking ${key}: hasKey=${hasKey}, isNotSum=${isNotSum}, value=${metrics[key]}`);
    if (hasKey && isNotSum) {
      items.push({
        name: config.label,
        value: Number(metrics[key]),
      });
    }
  }

  // If we found component values, calculate percentages and return as METRIC breakdown
  console.log('[buildComponents] METRIC items after component map:', JSON.stringify(items));
  if (items.length > 0) {
    // Calculate total for percentage computation (used by LENDER level)
    const totalValue = items.reduce((sum, item) => sum + (item.value || 0), 0);

    // Add percentage to each item so LENDER level has data to display
    const itemsWithPercentage = items.map(item => ({
      ...item,
      percentage: totalValue > 0 ? ((item.value || 0) / totalValue) * 100 : 0,
    }));

    console.log('[buildComponents] METRIC items with percentages:', JSON.stringify(itemsWithPercentage));
    return {
      count: items.length,
      breakdown_type: 'METRIC' as const,
      items: itemsWithPercentage,
    };
  }

  // Fallback: use transaction counts for CATEGORY breakdown
  if (metrics.income_txn_count) items.push({ name: 'Income Transactions', count: Number(metrics.income_txn_count) });
  if (metrics.expense_txn_count) items.push({ name: 'Expense Transactions', count: Number(metrics.expense_txn_count) });
  if (metrics.debt_payment_count) items.push({ name: 'Debt Payments', count: Number(metrics.debt_payment_count) });
  if (metrics.transfer_count) items.push({ name: 'Transfers', count: Number(metrics.transfer_count) });
  if (metrics.txn_count) items.push({ name: 'Total Transactions', count: Number(metrics.txn_count) });

  // If still no items, show all available numeric metrics
  if (items.length === 0) {
    console.log('[buildComponents] Entering numeric metrics fallback');
    for (const [key, val] of Object.entries(metrics)) {
      const isNumber = typeof val === 'number';
      const isNotTxnCount = !key.includes('txn_count');
      console.log(`[buildComponents] Fallback check: key=${key}, val=${val}, typeof=${typeof val}, isNumber=${isNumber}, isNotTxnCount=${isNotTxnCount}`);
      if (isNumber && isNotTxnCount) {
        const item = {
          name: formatMetricName(key),
          value: val,
        };
        console.log(`[buildComponents] Fallback pushing item:`, JSON.stringify(item));
        items.push(item);
      }
    }
    console.log('[buildComponents] Fallback items after loop:', JSON.stringify(items));
    if (items.length > 0) {
      // Calculate percentages for LENDER level display
      const totalValue = items.reduce((sum, item) => sum + Math.abs(item.value || 0), 0);
      const itemsWithPercentage = items.map(item => ({
        ...item,
        percentage: totalValue > 0 ? (Math.abs(item.value || 0) / totalValue) * 100 : 0,
      }));
      const result = {
        count: items.length,
        breakdown_type: 'METRIC' as const,
        items: itemsWithPercentage,
      };
      console.log('[buildComponents] Returning METRIC breakdown from fallback:', JSON.stringify(result));
      return result;
    }
  }

  return {
    count: items.reduce((sum, i) => sum + (i.count || 0), 0),
    breakdown_type: 'CATEGORY' as const,
    items,
  };
}

function formatMetricName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/(\d+)m/g, '($1 mo)')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format a numeric value based on its metric key for display
 * BUG-UI-046 FIX: Added 'checking' and 'total' to currency detection
 */
function formatCalculationValue(key: string, value: number): string {
  // Currency fields - BUG-UI-046: Added 'checking' and 'total' (e.g., checking_total, total_cash_on_hand)
  if (key.includes('income') || key.includes('payment') || key.includes('debt') ||
      key.includes('balance') || key.includes('deposits') || key.includes('withdrawals') ||
      key.includes('money') || key.includes('savings') || key.includes('expenses') ||
      key.includes('checking') || key.includes('total') || key.includes('cash') ||
      key === 'min_debt_payments' || key === 'monthly_gross_income' || key === 'liquid_balance' ||
      key === 'net_savings' || key === 'net_cash_flow' || key === 'money_left_over') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }
  // Percentage/ratio fields
  if (key.includes('ratio') || key.includes('pct') || key.includes('rate') || key.includes('margin')) {
    return `${value.toFixed(1)}%`;
  }
  // Score fields (0-100)
  if (key === 'score' || key === 'score_float' || key === 'factor_score') {
    return value.toFixed(1);
  }
  // Count fields
  if (key.includes('count') || key === 'months_coverage' || key.includes('payments')) {
    return value.toLocaleString();
  }
  // Default: show with appropriate precision
  return value >= 1000 ? value.toLocaleString() : value.toFixed(2);
}

/**
 * BUG-UI-032: Build VeraScore calculation steps showing score × weight = contribution
 * This makes the weighted sum formula clear to users
 */
function buildVeraScoreCalculationSteps(
  metrics: Record<string, unknown>
): Array<{ order: number; name: string; detail: string; intermediate_value?: number }> {
  const steps: Array<{ order: number; name: string; detail: string; intermediate_value?: number }> = [];
  let order = 1;

  // BUG-UI-051 FIX: Check which scoring system was used
  const scoringSystem = metrics['scoring_system'] as string | undefined;

  if (scoringSystem === 'curve') {
    // =========================================================================
    // CURVE-BASED SCORING: 2-stage calculation
    // Stage 1: other_composite = (DTI×0.20 + LR×0.125 + Debt×0.175 + PH×0.10) / 0.595
    // Stage 2: score = CF×0.15 + Savings×0.12 + LR×0.12 + other_composite×0.61
    // =========================================================================

    // Stage 1 header
    steps.push({
      order: order++,
      name: '📊 Stage 1: Other Composite',
      detail: 'Normalize DTI, Debt, PaymentHistory into single composite',
    });

    // Stage 1 factors
    const stage1Factors = [
      { name: 'DTI', scoreKey: 'stage1_debt_to_income_score', weightKey: 'stage1_debt_to_income_weight' },
      { name: 'Liquid Reserves', scoreKey: 'stage1_liquid_reserves_score', weightKey: 'stage1_liquid_reserves_weight' },
      { name: 'Debt Management', scoreKey: 'stage1_debt_management_score', weightKey: 'stage1_debt_management_weight' },
      { name: 'Payment History', scoreKey: 'stage1_payment_history_score', weightKey: 'stage1_payment_history_weight' },
    ];

    let stage1Sum = 0;
    for (const factor of stage1Factors) {
      const score = metrics[factor.scoreKey] as number | undefined;
      const weight = metrics[factor.weightKey] as number | undefined;
      if (typeof score === 'number' && typeof weight === 'number') {
        const contribution = score * weight;
        stage1Sum += contribution;
        steps.push({
          order: order++,
          name: `  ${factor.name}`,
          detail: `${score} × ${weight.toFixed(3)} = ${contribution.toFixed(2)}`,
          intermediate_value: contribution,
        });
      }
    }

    const divisor = metrics['stage1_divisor'] as number || 0.595;
    const otherComposite = metrics['other_composite'] as number;
    steps.push({
      order: order++,
      name: '  → Other Composite',
      detail: `(${stage1Sum.toFixed(2)}) / ${divisor} = ${otherComposite}`,
      intermediate_value: otherComposite,
    });

    // Stage 2 header
    steps.push({
      order: order++,
      name: '📊 Stage 2: Final Score',
      detail: 'Combine curve factors with other_composite',
    });

    // Stage 2 factors
    const stage2Factors = [
      { name: 'Cash Flow', scoreKey: 'cash_flow_score', weightKey: 'cash_flow_weight' },
      { name: 'Savings', scoreKey: 'savings_score', weightKey: 'savings_weight' },
      { name: 'Liquid Reserves', scoreKey: 'liquid_reserves_score', weightKey: 'liquid_reserves_weight' },
    ];

    let stage2Sum = 0;
    for (const factor of stage2Factors) {
      const score = metrics[factor.scoreKey] as number | undefined;
      const weight = metrics[factor.weightKey] as number | undefined;
      if (typeof score === 'number' && typeof weight === 'number') {
        const contribution = score * weight;
        stage2Sum += contribution;
        steps.push({
          order: order++,
          name: `  ${factor.name}`,
          detail: `${score} × ${weight.toFixed(2)} = ${contribution.toFixed(2)}`,
          intermediate_value: contribution,
        });
      }
    }

    // Add other_composite contribution
    const otherWeight = metrics['other_composite_weight'] as number || 0.61;
    if (typeof otherComposite === 'number') {
      const otherContribution = otherComposite * otherWeight;
      stage2Sum += otherContribution;
      steps.push({
        order: order++,
        name: '  Other Composite',
        detail: `${otherComposite} × ${otherWeight.toFixed(2)} = ${otherContribution.toFixed(2)}`,
        intermediate_value: otherContribution,
      });
    }

    // Final score
    const finalScore = metrics['score'] as number;
    if (typeof finalScore === 'number') {
      steps.push({
        order: order++,
        name: '✓ VeraScore',
        detail: `round(${stage2Sum.toFixed(2)}) = ${finalScore}`,
        intermediate_value: finalScore,
      });
    }

  } else {
    // =========================================================================
    // LINEAR SCORING: Simple weighted sum of 6 factors
    // =========================================================================
    let totalContribution = 0;

    const factors = [
      { name: 'Debt-to-Income', scoreKey: 'debt_to_income_score', weightKey: 'debt_to_income_weight' },
      { name: 'Cash Flow', scoreKey: 'cash_flow_score', weightKey: 'cash_flow_weight' },
      { name: 'Liquid Reserves', scoreKey: 'liquid_reserves_score', weightKey: 'liquid_reserves_weight' },
      { name: 'Savings', scoreKey: 'savings_score', weightKey: 'savings_weight' },
      { name: 'Debt Management', scoreKey: 'debt_management_score', weightKey: 'debt_management_weight' },
      { name: 'Payment History', scoreKey: 'payment_history_score', weightKey: 'payment_history_weight' },
    ];

    for (const factor of factors) {
      const score = metrics[factor.scoreKey];
      const weight = metrics[factor.weightKey];

      if (typeof score === 'number' && typeof weight === 'number') {
        const contribution = score * weight;
        totalContribution += contribution;

        steps.push({
          order: order++,
          name: factor.name,
          detail: `${score} × ${weight.toFixed(2)} = ${contribution.toFixed(2)}`,
          intermediate_value: contribution,
        });
      }
    }

    if (steps.length > 0) {
      steps.push({
        order: order++,
        name: 'Weighted Sum',
        detail: `Σ contributions = ${totalContribution.toFixed(2)}`,
        intermediate_value: totalContribution,
      });
    }

    const finalScore = metrics['score'];
    if (typeof finalScore === 'number') {
      steps.push({
        order: order++,
        name: 'VeraScore',
        detail: `round(${totalContribution.toFixed(2)}) = ${finalScore}`,
        intermediate_value: finalScore,
      });
    }
  }

  // If no steps were built, provide helpful message
  if (steps.length === 0) {
    steps.push({
      order: 1,
      name: 'Calculation Data',
      detail: 'Factor scores and weights will be shown once evidence is populated.',
    });
  }

  return steps;
}

/**
 * Build calculation steps from intermediate_metrics for display in Calculation tab.
 * Shows actual calculated values, not generic pipeline steps.
 */
function buildCalculationSteps(
  entityName: string,
  metrics: Record<string, unknown>
): Array<{ order: number; name: string; detail: string; intermediate_value?: number }> {
  const steps: Array<{ order: number; name: string; detail: string; intermediate_value?: number }> = [];
  let order = 1;

  // Define metric-specific calculation steps with actual values
  // BUG-UI-039: Enhanced curve_lookup explanations with threshold guidance
  // BUG-UI-038/044: Keys MUST match backend field names exactly
  // BUG-UI-073: Added optional 'aliases' array to handle backend key variations (e.g., saving vs savings)
  const stepConfigs: Record<string, Array<{ key: string; aliases?: string[]; name: string; detailTemplate: string }>> = {
    // DTI Factor calculation steps - BUG-UI-038: Complete calculation chain
    // Backend emits: yearly_gross_income, minimum_payments_all_debt, dti_ratio, score, rating
    'debt_to_income_factor': [
      { key: 'yearly_gross_income', name: '1. Yearly Gross Income', detailTemplate: 'Annual income from all sources = {value}' },
      { key: 'monthly_gross_income', name: '2. Monthly Gross Income', detailTemplate: 'yearly_income / 12 = {value}' },
      { key: 'minimum_payments_all_debt', name: '3. Monthly Debt Payments', detailTemplate: 'Sum of all minimum monthly debt payments = {value}' },
      { key: 'dti_ratio', name: '4. DTI Ratio', detailTemplate: '(monthly_debt / monthly_income) × 100 = {value}' },
      { key: 'score', name: '5. DTI Score', detailTemplate: 'Score = {value} (DTI <20%→100, 20-36%→60-100, 36-50%→0-60, >50%→0)' },
    ],
    // Cash Flow Factor calculation steps
    'cash_flow_factor': [
      { key: 'money_in', name: 'Money In', detailTemplate: 'Total income for period = {value}' },
      { key: 'money_out', name: 'Money Out', detailTemplate: 'Total expenses for period = {value}' },
      { key: 'money_left_over', name: 'Money Left Over', detailTemplate: 'money_in - money_out = {value}' },
      { key: 'cash_flow_pct', name: 'Cash Flow %', detailTemplate: 'money_left_over / money_in × 100 = {value}' },
      { key: 'score', name: 'Cash Flow Score', detailTemplate: 'Score = {value} (CF% >20%→100, 10-20%→70-90, 0-10%→30-70, <0%→0-30)' },
    ],
    // BUG-UI-041: Liquid Reserves Factor calculation steps - use correct backend field names
    // Backend emits: liquid_reserves_balance, other_monthly_expenses, minimum_payments_all_debt, months_reserves
    'liquid_reserves_factor': [
      { key: 'liquid_reserves_balance', name: 'Liquid Reserves Balance', detailTemplate: 'Sum of checking + savings balances = {value}' },
      { key: 'other_monthly_expenses', name: 'Other Monthly Expenses', detailTemplate: 'Non-debt monthly expenses = {value}' },
      { key: 'minimum_payments_all_debt', name: 'Min Debt Payments', detailTemplate: 'Monthly minimum debt payments = {value}' },
      { key: 'total_monthly_expenses', name: 'Total Monthly Expenses', detailTemplate: 'other_expenses + min_debt_payments = {value}' },
      { key: 'months_reserves', name: 'Months of Reserves', detailTemplate: 'liquid_balance / total_expenses = {value} months' },
      { key: 'score', name: 'Reserves Score', detailTemplate: 'Score = {value} (>12mo→100, 6-12mo→70-90, 3-6mo→40-70, <3mo→0-40)' },
    ],
    // Savings Factor calculation steps
    'savings_factor': [
      { key: 'net_savings', name: 'Net Savings', detailTemplate: 'Total net savings for period = {value}' },
      { key: 'yearly_gross_income', name: 'Yearly Income', detailTemplate: 'Annualized gross income = {value}' },
      { key: 'savings_rate', name: 'Savings Rate', detailTemplate: 'net_savings / yearly_income × 100 = {value}' },
      { key: 'score', name: 'Savings Score', detailTemplate: 'Score = {value} (>20%→100, 10-20%→60-90, 0-10%→20-60, <0%→0-20)' },
    ],
    // Debt Management Factor calculation steps - BUG-UI-039: Enhanced scoring explanation
    'debt_management_factor': [
      { key: 'short_term_debt_balance', name: 'Short-Term Debt', detailTemplate: 'Credit card + short-term debt balance = {value}' },
      { key: 'short_term_debt_payments', name: 'Debt Payments Made', detailTemplate: 'Payments toward short-term debt = {value}' },
      { key: 'paid_ratio', name: 'Paid Ratio', detailTemplate: 'payments / balance = {value}' },
      { key: 'score', name: 'Debt Mgmt Score', detailTemplate: 'Score = {value} (paying>balance→100, >50%→70-90, >25%→40-70, <25%→0-40)' },
    ],
    // Payment History Factor calculation steps - BUG-UI-039: Enhanced scoring explanation
    'payment_history_factor': [
      { key: 'number_of_late_payments', name: 'Late Payments', detailTemplate: 'Count of late payments in period = {value}' },
      { key: 'age_of_late_payments', name: 'Age of Late Payments', detailTemplate: 'Months since most recent late payment = {value}' },
      { key: 'score', name: 'Payment History Score', detailTemplate: 'Score = {value} (0 late→100, 1 late >12mo→80, recent late→0-50)' },
    ],
    // BUG-UI-065/066: DTI Ratio metric calculation steps - inputs before result
    'dti_ratio': [
      { key: 'yearly_gross_income', name: 'Yearly Gross Income', detailTemplate: 'Annual income from all sources = {value}' },
      { key: 'monthly_gross_income', name: 'Monthly Gross Income', detailTemplate: 'yearly_income / 12 = {value}' },
      { key: 'minimum_payments_all_debt', name: 'Monthly Debt Payments', detailTemplate: 'Sum of all minimum monthly debt payments = {value}' },
      { key: 'dti_ratio', name: 'DTI Ratio', detailTemplate: '(monthly_debt / monthly_income) × 100 = {value}' },
    ],
    // Money In metric - BUG-UI-048: Added investment_deposits to match formula
    // BUG-UI-073 FIX: Added 'aliases' array to handle backend key variations (saving vs savings)
    'money_in_1m': [
      { key: 'checking_deposits', name: 'Checking Deposits', detailTemplate: 'Deposits to checking accounts = {value}' },
      { key: 'saving_deposits', aliases: ['savings_deposits'], name: 'Savings Deposits', detailTemplate: 'Deposits to savings accounts = {value}' },
      { key: 'investment_deposits', name: 'Investment Deposits', detailTemplate: 'Deposits to investment accounts = {value}' },
      { key: 'sum_deposits', name: 'Total Money In', detailTemplate: 'checking + savings + investment = {value}' },
    ],
    // Money Out metric - BUG-UI-048: Added investment_withdrawals for consistency
    // BUG-UI-073 FIX: Added 'aliases' array to handle backend key variations (saving vs savings)
    'money_out_1m': [
      { key: 'checking_withdrawals', name: 'Checking Withdrawals', detailTemplate: 'Withdrawals from checking = {value}' },
      { key: 'saving_withdrawals', aliases: ['savings_withdrawals'], name: 'Savings Withdrawals', detailTemplate: 'Withdrawals from savings = {value}' },
      { key: 'investment_withdrawals', name: 'Investment Withdrawals', detailTemplate: 'Withdrawals from investment = {value}' },
      { key: 'sum_withdrawals', name: 'Total Money Out', detailTemplate: 'checking + savings + investment = {value}' },
    ],
    // BUG-UI-046/075: Cash on Hand calculation steps - account counts then totals
    'cash_on_hand': [
      { key: 'account_count', name: 'Linked Accounts', detailTemplate: 'Number of linked bank accounts = {value}' },
      { key: 'savings_total', name: 'Savings Balance', detailTemplate: 'Sum of savings account balances = {value}' },
      { key: 'checking_total', name: 'Checking Balance', detailTemplate: 'Sum of checking account balances = {value}' },
      { key: 'total_cash_on_hand', name: 'Total Cash on Hand', detailTemplate: 'savings + checking = {value}' },
    ],
    // BUG-UI-029: Yearly Gross Income (6-month average × 2 = annualized)
    'gross_income_6m': [
      { key: 'sum_money_in', name: '6-Month Total Income', detailTemplate: 'Sum of income over 6 months = {value}' },
      { key: 'monthly_average', name: 'Monthly Average', detailTemplate: '6-month total / 6 = {value}' },
      { key: 'yearly_gross_income', name: 'Yearly Gross Income', detailTemplate: 'monthly average × 12 = {value}' },
    ],
    // BUG-UI-032: VeraScore calculation steps - show weighted sum of factors
    'verascore_current': [
      { key: 'income_stability_score', name: 'Income Stability', detailTemplate: 'Factor score = {value}' },
      { key: 'cash_flow_score', name: 'Cash Flow', detailTemplate: 'Factor score = {value}' },
      { key: 'liquid_reserves_score', name: 'Liquid Reserves', detailTemplate: 'Factor score = {value}' },
      { key: 'savings_score', name: 'Savings', detailTemplate: 'Factor score = {value}' },
      { key: 'debt_management_score', name: 'Debt Management', detailTemplate: 'Factor score = {value}' },
      { key: 'payment_history_score', name: 'Payment History', detailTemplate: 'Factor score = {value}' },
      { key: 'weighted_sum', name: 'Weighted Sum', detailTemplate: 'Σ(factor × weight) = {value}' },
      { key: 'score', name: 'VeraScore', detailTemplate: 'Final score = {value}' },
    ],
  };

  // Get config for this entity, or use generic steps
  const config = stepConfigs[entityName];

  // DEBUG: Log what we're building steps from
  console.log(`🔧 [buildCalculationSteps] entityName=${entityName}, metrics keys:`, Object.keys(metrics));

  // BUG-UI-032: Special handling for VeraScore to show score × weight = contribution
  if (entityName === 'verascore_current') {
    return buildVeraScoreCalculationSteps(metrics);
  }

  // BUG-UI-038/041: Derive missing fields for complete calculation chain
  const enrichedMetrics = { ...metrics };

  // DTI: Calculate monthly_gross_income from yearly if not provided
  if (entityName === 'debt_to_income_factor') {
    const yearlyIncome = metrics['yearly_gross_income'] as number | undefined;
    if (typeof yearlyIncome === 'number' && !metrics['monthly_gross_income']) {
      enrichedMetrics['monthly_gross_income'] = yearlyIncome / 12;
      console.log(`🔧 [buildCalculationSteps] Derived monthly_gross_income = ${yearlyIncome} / 12 = ${enrichedMetrics['monthly_gross_income']}`);
    }
  }

  // BUG-UI-041: Liquid Reserves - Calculate total_monthly_expenses if not provided
  if (entityName === 'liquid_reserves_factor') {
    const otherExpenses = metrics['other_monthly_expenses'] as number | undefined;
    const minDebt = metrics['minimum_payments_all_debt'] as number | undefined;
    if (typeof otherExpenses === 'number' && typeof minDebt === 'number' && !metrics['total_monthly_expenses']) {
      enrichedMetrics['total_monthly_expenses'] = otherExpenses + minDebt;
      console.log(`🔧 [buildCalculationSteps] Derived total_monthly_expenses = ${otherExpenses} + ${minDebt} = ${enrichedMetrics['total_monthly_expenses']}`);
    }
  }

  // BUG-UI-029: Gross Income 6m - Derive monthly_average and yearly_gross_income from sum
  if (entityName === 'gross_income_6m') {
    const sumMoneyIn = metrics['sum_money_in'] as number | undefined;
    if (typeof sumMoneyIn === 'number') {
      if (!metrics['monthly_average']) {
        enrichedMetrics['monthly_average'] = sumMoneyIn / 6;
        console.log(`🔧 [buildCalculationSteps] Derived monthly_average = ${sumMoneyIn} / 6 = ${enrichedMetrics['monthly_average']}`);
      }
      if (!metrics['yearly_gross_income']) {
        const monthlyAvg = enrichedMetrics['monthly_average'] || sumMoneyIn / 6;
        enrichedMetrics['yearly_gross_income'] = monthlyAvg * 12;
        console.log(`🔧 [buildCalculationSteps] Derived yearly_gross_income = ${monthlyAvg} × 12 = ${enrichedMetrics['yearly_gross_income']}`);
      }
    }
  }

  // BUG-UI-041: Track which keys we've shown so we can add any missing ones
  const shownKeys = new Set<string>();

  if (config) {
    // Build steps from config, only including metrics that have values
    for (const stepConfig of config) {
      // BUG-UI-073 FIX: Check primary key first, then check aliases
      let value = enrichedMetrics[stepConfig.key];
      let matchedKey = stepConfig.key;

      // If primary key not found, check aliases
      if (value === undefined && stepConfig.aliases) {
        for (const alias of stepConfig.aliases) {
          if (enrichedMetrics[alias] !== undefined) {
            value = enrichedMetrics[alias];
            matchedKey = alias;
            console.log(`🔧 [buildCalculationSteps] Using alias ${alias} for ${stepConfig.key}`);
            break;
          }
        }
      }

      console.log(`🔧 [buildCalculationSteps] Checking ${stepConfig.key}: value=${value}, type=${typeof value}`);
      if (value !== undefined && typeof value === 'number') {
        const formattedValue = formatCalculationValue(matchedKey, value);

        // BUG-UI-048: Build dynamic formula for sum fields based on available components
        let detail = stepConfig.detailTemplate.replace('{value}', formattedValue);
        if (stepConfig.key === 'sum_deposits' || stepConfig.key === 'sum_withdrawals') {
          const components: string[] = [];
          if (typeof enrichedMetrics['checking_deposits'] === 'number' || typeof enrichedMetrics['checking_withdrawals'] === 'number') {
            components.push('checking');
          }
          if (typeof enrichedMetrics['savings_deposits'] === 'number' || typeof enrichedMetrics['saving_deposits'] === 'number' ||
              typeof enrichedMetrics['savings_withdrawals'] === 'number' || typeof enrichedMetrics['saving_withdrawals'] === 'number') {
            components.push('savings');
          }
          if (typeof enrichedMetrics['investment_deposits'] === 'number' || typeof enrichedMetrics['investment_withdrawals'] === 'number') {
            components.push('investment');
          }
          if (components.length > 0) {
            detail = `${components.join(' + ')} = ${formattedValue}`;
          }
        }

        steps.push({
          order: order++,
          name: stepConfig.name,
          detail,
          intermediate_value: value,
        });
        // BUG-UI-073 FIX: Mark both primary key AND matched key as shown to prevent duplicates
        shownKeys.add(stepConfig.key);
        if (matchedKey !== stepConfig.key) {
          shownKeys.add(matchedKey);
        }
        // Also mark aliases as shown
        if (stepConfig.aliases) {
          stepConfig.aliases.forEach(alias => shownKeys.add(alias));
        }
      }
    }
  }

  // BUG-UI-041: Also add any numeric metrics NOT in config (ensures all data is shown)
  // Exclude metadata fields that shouldn't be displayed as steps
  const excludeKeys = new Set(['scoring_method', 'rating', 'formula', 'txn_count', 'score_float']);

  // Use enrichedMetrics to include derived values
  for (const [key, value] of Object.entries(enrichedMetrics)) {
    if (typeof value === 'number' && !excludeKeys.has(key) && !shownKeys.has(key)) {
      // Only add if we haven't already shown it via config
      const formattedValue = formatCalculationValue(key, value);
      steps.push({
        order: order++,
        name: formatMetricName(key),
        detail: `${key} = ${formattedValue}`,
        intermediate_value: value,
      });
      shownKeys.add(key);
    }
  }

  // If still no steps, provide a helpful message
  if (steps.length === 0) {
    steps.push({
      order: 1,
      name: 'Calculation Data',
      detail: 'Intermediate calculation values will be shown once evidence is populated.',
    });
  }

  console.log(`🔧 [buildCalculationSteps] Built ${steps.length} steps for ${entityName}`);
  return steps;
}

function buildFormula(entityName: string): string {
  const formulas: Record<string, string> = {
    // Money metrics
    'money_in_1m': 'checking_deposits + savings_deposits + investment_deposits',
    'money_out_1m': 'checking_withdrawals + savings_withdrawals + investment_withdrawals',
    'money_in': 'checking_deposits + savings_deposits + investment_deposits',
    'money_out': 'checking_withdrawals + savings_withdrawals + investment_withdrawals',
    'money_leftover_1m': 'money_in_1m - money_out_1m',
    'gross_income_1m': 'SUM(amount) WHERE role = INCOME for month',
    // BUG-UI-029: Yearly income formula
    'gross_income_6m': '(SUM(income_transactions, 6 months) / 6) × 12',
    'total_expenses_1m': 'SUM(ABS(amount)) WHERE role = EXPENSE for month',
    'total_expenses_6m': 'SUM(ABS(amount)) WHERE role = EXPENSE for last 6 months',
    // Ratios
    'dti_ratio': 'monthly_debt_payments / monthly_income',
    // Factors - BUG-UI-059: Use curve_lookup for factors (not weighted_score)
    'cash_flow_factor': 'curve_lookup(cash_flow_pct)',
    'debt_to_income_factor': 'curve_lookup(dti_ratio)',
    'liquid_reserves_factor': 'curve_lookup(months_reserves)',
    'savings_factor': 'curve_lookup(savings_rate)',
    'debt_management_factor': 'curve_lookup(paid_ratio)',
    'payment_history_factor': 'curve_lookup(late_payment_age, late_payment_count)',
    // VeraScore - BUG-UI-032: Clearer formula showing the weighted sum
    'verascore_current': 'Σ(factor_score × weight) for all 6 factors',
  };
  return formulas[entityName] || `Calculate ${entityName}`;
}

function buildSources(transactions: TransactionContributionResponse) {
  const allMonths = transactions.months || [];

  // Build included transactions
  const included = allMonths.flatMap(month =>
    month.top_contributors
      .filter(tc => tc.role !== 'EXCLUDED')
      .map(tc => ({
        tx_id: tc.transaction_id,
        date: tc.date,
        amount: tc.amount,
        merchant: tc.description,
        category: tc.category,
        direction: tc.amount >= 0 ? 'credit' as const : 'debit' as const,
      }))
  ).slice(0, 100);

  // Build excluded transactions
  const excluded = allMonths.flatMap(month =>
    month.excluded_transaction_ids.map(txId => ({
      tx_id: txId,
      reason: (month.exclusion_reasons[txId] || 'out_of_window') as any,
      amount: undefined,
    }))
  ).slice(0, 50);

  return {
    transactions: {
      included_count: transactions.total_transactions,
      excluded_count: transactions.total_excluded,
      included,
      excluded,
    },
  };
}
